<template>
    <div class="promotion-content-management">
        <!-- 页面头部 -->
        <div class="header">
            <div class="header-left">
                <button class="back-btn" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                </button>
                <div class="title">攻略管理</div>
            </div>
        </div>

        <!-- 操作工具栏 -->
        <div class="toolbar">
            <div class="toolbar-right">
                <div class="search-filters">
                    <div style="display: flex; gap: 10px;">
                        <el-select v-model="searchCategory" @change="handleSearch" placeholder="全部分类" clearable>
                            <el-option label="全部分类" value="" />
                            <el-option v-for="category in categories" :key="category" :label="category"
                                :value="category" />
                        </el-select>

                        <el-select v-model="searchInvalidStatus" @change="handleSearch" placeholder="失效状态" clearable>
                            <el-option label="全部状态" value="" />
                            <el-option label="待处理" value="REPORTED" />
                            <el-option label="已处理" value="CONFIRMED" />
                            <el-option label="已驳回" value="REJECTED" />
                            <el-option label="未上报" value="NONE" />
                        </el-select>
                    </div>

                    <el-input v-model="searchKeyword" @input="handleSearch" placeholder="搜索推广内容..." class="search-input"
                        clearable />

                    <button @click="handleSearch" class="btn btn-secondary">
                        <i class="icon-search"></i> 搜索
                    </button>
                    <button @click="showAddModal = true" class="btn btn-primary">
                        <i class="icon-plus"></i> 新增
                    </button>
                    <button @click="batchDelete" class="btn btn-danger" :disabled="selectedItems.length === 0">
                        <i class="icon-delete"></i> 删除
                    </button>
                    <button @click="batchUpdateStatus(true)" class="btn btn-success"
                        :disabled="selectedItems.length === 0">
                        <i class="icon-check"></i> 启用
                    </button>
                    <button @click="batchUpdateStatus(false)" class="btn btn-warning"
                        :disabled="selectedItems.length === 0">
                        <i class="icon-close"></i> 禁用
                    </button>
                    <button @click="showSyncConfig" class="btn btn-warning">
                        <i class="icon-refresh"></i> 同步
                    </button>
                </div>
            </div>
        </div>

        <!-- 内容列表 -->
        <div class="content-table-container">
            <el-table :data="contentList" style="width: 100%;" height="calc(100vh - 210px)"
                @selection-change="handleSelectionChange" :row-class-name="getRowClassName" empty-text="暂无推广内容"
                v-loading="loading">
                <el-table-column type="selection" width="55" align="center"></el-table-column>
                <el-table-column prop="title" label="标题" width="120" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span>{{ scope.row.title || '--' }}</span>
                    </template>
                </el-table-column>

                <el-table-column prop="skuName" label="名称" width="120" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span>{{ scope.row.skuName || '--' }}</span>
                    </template>
                </el-table-column>

                <el-table-column prop="category" label="分类" width="100" align="center">
                    <template slot-scope="scope">
                        <el-tag size="small" type="primary">{{ scope.row.category }}</el-tag>
                    </template>
                </el-table-column>

                <el-table-column prop="imageUrl" label="图片" width="100" align="center">
                    <template slot-scope="scope">
                        <img :src="scope.row.imageUrl" alt="图片" style="width: 30px;height: 30px;">
                    </template>
                </el-table-column>

                <el-table-column prop="isActive" label="状态" width="70" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.isActive ? 'success' : 'danger'" size="small">
                            {{ scope.row.isActive ? '启用' : '禁用' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column prop="invalidStatus" label="失效状态" width="100" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="getInvalidStatusType(scope.row.invalidStatus)" size="small">
                            {{ getInvalidStatusText(scope.row.invalidStatus) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column prop="content" label="内容" align="center" min-width="300">
                    <template slot-scope="scope">
                        <div style="height: 100px;overflow-y: auto;">
                            <div v-html="scope.row.content"></div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column prop="reportedBy" label="上报人" width="100" align="center" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span>{{ scope.row.reportedBy || '--' }}</span>
                    </template>
                </el-table-column>

                <el-table-column prop="reportedTime" label="上报时间" width="150" align="center">
                    <template slot-scope="scope">
                        <span>{{ formatTime(scope.row.reportedTime) || '--' }}</span>
                    </template>
                </el-table-column>

                <el-table-column prop="invalidReason" label="失效原因" width="150" align="center" show-overflow-tooltip>
                    <template slot-scope="scope">
                        <span>{{ scope.row.invalidReason || '--' }}</span>
                    </template>
                </el-table-column>

                <el-table-column prop="updatedTime" label="更新时间" width="150" align="center">
                    <template slot-scope="scope">
                        <span>{{ formatTime(scope.row.updatedTime) }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="createdTime" label="创建时间" width="150" align="center">
                    <template slot-scope="scope">
                        <span>{{ formatTime(scope.row.createdTime) }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="操作" width="160" align="center" fixed="right">
                    <template slot-scope="scope">
                        <el-button size="mini" type="info" @click="viewContent(scope.row)">
                            查看
                        </el-button>
                        <el-button size="mini" type="primary" @click="editContent(scope.row)">
                            编辑
                        </el-button>
                        <el-button size="mini" type="danger" @click="deleteContent(scope.row.id)">
                            删除
                        </el-button>
                        <template v-if="scope.row.invalidStatus === 'REPORTED'">
                            <el-button size="mini" type="success" @click="confirmInvalidReport(scope.row)">
                                确认失效
                            </el-button>
                            <el-button size="mini" type="warning" @click="rejectInvalidReport(scope.row)">
                                驳回
                            </el-button>
                        </template>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 分页 -->
        <div class="pagination-container" v-if="totalPages > 1">
            <el-pagination @current-change="handlePageChange" :current-page="currentPage + 1" :page-size="pageSize"
                :total="totalElements" :page-sizes="[20, 50, 100]" @size-change="handleSizeChange"
                layout="total, sizes, prev, pager, next, jumper" background>
            </el-pagination>
        </div>

        <!-- 新增/编辑弹窗 -->
        <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content enhanced-modal" @click.stop>
                <div class="modal-header">
                    <div class="modal-title">
                        {{ showAddModal ? '新增推广内容' : '编辑推广内容' }}
                    </div>
                    <button @click="closeModal" class="modal-close">
                        <i class="el-icon-close"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <el-form :model="formData" :rules="formRules" ref="contentForm" label-width="80px"
                        class="enhanced-form">
                        <el-form-item label="分类" prop="category">
                            <el-select v-model="formData.category" placeholder="请选择分类" clearable>
                                <el-option label="请选择分类" value="" />
                                <el-option v-for="category in categories" :key="category" :label="category"
                                    :value="category" />
                                <el-option label="自定义分类" value="custom" />
                            </el-select>
                            <el-input v-if="formData.category === 'custom'" v-model="customCategory"
                                placeholder="请输入自定义分类" style="margin-top: 8px;" clearable />
                        </el-form-item>

                        <el-form-item label="标题" prop="title">
                            <el-input v-model="formData.title" placeholder="请输入标题" maxlength="200" clearable />
                            <div class="input-counter">{{ formData.title.length }}/200</div>
                        </el-form-item>

                        <el-form-item label="内容" prop="content">
                            <div class="content-editor-wrapper">
                                <quill-editor v-model="formData.content" :options="editorOption"
                                    class="content-quill-editor" placeholder="请输入推广文案内容">
                                </quill-editor>
                            </div>
                        </el-form-item>

                        <el-form-item label="商品SPU ID" prop="spuid">
                            <el-input v-model="formData.spuid" placeholder="请输入商品SPU ID" maxlength="100" clearable />
                        </el-form-item>

                        <el-form-item label="素材链接" prop="materialUrl">
                            <el-input v-model="formData.materialUrl" placeholder="请输入素材链接" maxlength="500" clearable />
                        </el-form-item>

                        <el-form-item label="商品ID" prop="itemId">
                            <el-input v-model="formData.itemId" placeholder="请输入商品ID" maxlength="100" clearable />
                        </el-form-item>

                        <el-form-item label="指导价格" prop="guidePrice">
                            <el-input v-model="formData.guidePrice" type="number" step="0.01" placeholder="请输入指导价格"
                                clearable />
                        </el-form-item>

                        <el-form-item label="启用状态" prop="isActive">
                            <el-switch v-model="formData.isActive" />
                        </el-form-item>
                    </el-form>
                </div>

                <div class="modal-footer">
                    <button @click="closeModal" class="btn btn-secondary enhanced-btn">
                        取消
                    </button>
                    <button @click="saveContent" class="btn btn-primary enhanced-btn" :disabled="loading">
                        <div v-if="loading" class="loading-spinner"></div>
                        {{ loading ? '保存中...' : '保存' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 查看弹窗 -->
        <div v-if="showViewModal" class="modal-overlay" @click="closeViewModal">
            <div class="modal-content enhanced-modal" @click.stop style="width: 800px; max-height: 95vh;">
                <div class="modal-header">
                    <div class="modal-title">
                        查看推广内容
                    </div>
                    <button @click="closeViewModal" class="modal-close">
                        <i class="el-icon-close"></i>
                    </button>
                </div>

                <div class="modal-body" style="max-height: calc(90vh - 200px); overflow-y: auto;">
                    <div class="view-content-wrapper">
                        <div class="view-item">
                            <label>分类：</label>
                            <span>{{ viewData?.category || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>标题：</label>
                            <span>{{ viewData?.title || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>商品名称：</label>
                            <span>{{ viewData?.skuName || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>商品SPU ID：</label>
                            <span>{{ viewData?.spuid || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>商品ID：</label>
                            <span>{{ viewData?.itemId || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>素材链接：</label>
                            <span style="word-break: break-all;">{{ viewData?.materialUrl || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>指导价格：</label>
                            <span>{{ viewData?.guidePrice ? '¥' + viewData.guidePrice : '--' }}</span>
                        </div>
                        <div class="view-item" v-if="viewData?.imageUrl">
                            <label>商品图片：</label>
                            <img :src="viewData.imageUrl" alt="商品图片"
                                style="max-width: 200px; max-height: 200px; border-radius: 4px;" />
                        </div>
                        <div class="view-item">
                            <label>启用状态：</label>
                            <el-tag :type="viewData?.isActive ? 'success' : 'danger'" size="small">
                                {{ viewData?.isActive ? '启用' : '禁用' }}
                            </el-tag>
                        </div>
                        <div class="view-item">
                            <label>失效状态：</label>
                            <el-tag :type="getInvalidStatusType(viewData?.invalidStatus)" size="small">
                                {{ getInvalidStatusText(viewData?.invalidStatus) }}
                            </el-tag>
                        </div>
                        <div class="view-item" v-if="viewData?.reportedBy">
                            <label>上报人：</label>
                            <span>{{ viewData.reportedBy }}</span>
                        </div>
                        <div class="view-item" v-if="viewData?.reportedTime">
                            <label>上报时间：</label>
                            <span>{{ formatTime(viewData.reportedTime) }}</span>
                        </div>
                        <div class="view-item" v-if="viewData?.invalidReason">
                            <label>失效原因：</label>
                            <span>{{ viewData.invalidReason }}</span>
                        </div>
                        <div class="view-item">
                            <label>创建时间：</label>
                            <span>{{ formatTime(viewData?.createdTime) || '--' }}</span>
                        </div>
                        <div class="view-item">
                            <label>更新时间：</label>
                            <span>{{ formatTime(viewData?.updatedTime) || '--' }}</span>
                        </div>
                        <div class="view-item" style="flex-direction: column; align-items: flex-start;">
                            <label style="margin-bottom: 8px;">内容：</label>
                            <div class="view-content-text" v-html="viewData?.content || '--'"></div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button @click="closeViewModal" class="btn btn-secondary enhanced-btn" style="width: 100%;">
                        关闭
                    </button>
                </div>
            </div>
        </div>

        <!-- 同步预览弹窗 -->
        <div v-if="showSyncPreviewModal" class="modal-overlay" @click="closeSyncPreviewModal">
            <div class="modal-content enhanced-modal" @click.stop style=" width: 96%;height: 96%;">
                <div class="modal-header">
                    <div class="modal-title">
                        <span>同步预览（共 {{ syncPreviewData.length }} 条数据）</span>
                    </div>
                    <button @click="closeSyncPreviewModal" class="modal-close" icon="el-icon-close">
                        <i class="el-icon-close"></i>
                    </button>
                </div>

                <div class="modal-body difContent" style="overflow-y: auto;">
                    <div v-if="createdSpuids.length > 0" style="margin-top: 10px;">
                        <el-tag type="success" size="small">新增ItemId（{{ createdSpuids.length }} 个）：</el-tag>
                        <span style="margin-left: 8px; font-size: 12px; color: #42b983;">{{ createdSpuids.join(', ')
                        }}</span>
                    </div>
                    <el-table v-else ref="syncPreviewTable" :data="syncPreviewData" style="width: 100%" border
                        @selection-change="handleSyncSelectionChange">
                        <el-table-column type="selection" width="55" align="center"></el-table-column>
                        <el-table-column prop="content" label="内容" min-width="300">
                            <template slot-scope="scope">
                                <div class="content-editor-wrapper">
                                    <quill-editor v-model="scope.row.content" :options="editorOption"
                                        class="content-quill-editor">
                                    </quill-editor>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="title" label="标题" width="150" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <span>{{ scope.row.title || '--' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="category" label="分类" width="120" align="center">
                            <template slot-scope="scope">
                                <el-tag size="small" type="primary">{{ scope.row.category }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="imageUrl" label="商品图片" width="120" align="center">
                            <template slot-scope="scope">
                                <img v-if="scope.row.imageUrl" :src="scope.row.imageUrl" alt="商品图片"
                                    style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px; cursor: pointer;"
                                    @click="previewImage(scope.row.imageUrl)">
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="spuid" label="SPUID" width="150" show-overflow-tooltip></el-table-column>
                        <el-table-column prop="skuName" label="商品名称" width="150"
                            show-overflow-tooltip></el-table-column>
                        <el-table-column prop="itemId" label="ItemID" width="200"
                            show-overflow-tooltip></el-table-column>
                    </el-table>
                </div>

                <div class="modal-footer">
                    <button @click="closeSyncPreviewModal" class="btn btn-secondary enhanced-btn">
                        取消
                    </button>
                    <button v-if="createdSpuids.length > 0" @click="copyCreatedSpuids"
                        class="btn btn-primary enhanced-btn">复制</button>
                    <button v-else @click="confirmSync" class="btn btn-primary enhanced-btn" :disabled="loading">
                        <div v-if="loading" class="loading-spinner"></div>
                        {{ loading ? '同步中...' : '确认同步' }}
                    </button>

                </div>
            </div>
        </div>

        <!-- 同步配置弹窗 -->
        <div v-if="showSyncModal" class="modal-overlay" @click="closeSyncModal">
            <div class="modal-content enhanced-modal" @click.stop style="max-width: 800px;">
                <div class="modal-header">
                    <div class="modal-title">
                        同步配置
                    </div>
                    <button @click="closeSyncModal" class="modal-close">
                        <i class="el-icon-close"></i>
                    </button>
                </div>

                <div class="modal-body">
                    <div>
                        <div style="display: flex; gap: 16px; align-items: center; margin-bottom: 18px;">
                            <el-radio-group v-model="syncConfig.syncType" size="medium">
                                <el-radio-button label="api">接口同步</el-radio-button>
                                <el-radio-button label="url">链接同步</el-radio-button>
                                <el-radio-button label="data">数据同步</el-radio-button>
                            </el-radio-group>
                        </div>
                    </div>
                    <el-form :model="syncConfig" :rules="syncRules" ref="syncForm" label-width="85px"
                        class="enhanced-form">
                        <!-- 接口同步模式 -->
                        <template v-if="syncConfig.syncType === 'api'">
                            <el-form-item label="Token" prop="token">
                                <el-input v-model="syncConfig.token" type="textarea" :rows="3" placeholder="请输入Token"
                                    clearable />
                            </el-form-item>

                            <el-form-item label="页数数组" prop="maxPage">
                                <el-input v-model="syncConfig.maxPage" type="textarea" :rows="3"
                                    placeholder="请输入页数，支持逗号或换行分隔，如：1,2,3 或每行一个数字" clearable />
                            </el-form-item>

                            <el-form-item label="列表地址" prop="productUrl">
                                <el-input v-model="syncConfig.productUrl" placeholder="请输入商品接口地址" clearable />
                            </el-form-item>

                            <el-form-item label="详情地址" prop="courseUrl">
                                <el-input v-model="syncConfig.courseUrl" placeholder="请输入课程接口地址" clearable />
                            </el-form-item>
                        </template>

                        <!-- 链接同步模式 -->
                        <template v-if="syncConfig.syncType === 'url'">
                            <el-form-item label="关键词" prop="keyword">
                                <el-input v-model="syncConfig.keyword" type="textarea" :rows="3"
                                    placeholder="请输入关键词、商品链接" clearable />
                            </el-form-item>
                            <el-form-item label="内容" prop="iptContent">
                                <el-input v-model="syncConfig.iptContent" type="textarea" :rows="3"
                                    placeholder="请输入内容，将作为所有商品的content字段" clearable />
                            </el-form-item>
                        </template>

                        <!-- 数据同步模式 -->
                        <template v-if="syncConfig.syncType === 'data'">
                            <el-form-item label="数据数组" prop="dataArray">
                                <el-input v-model="syncConfig.dataArray" type="textarea" :rows="10"
                                    placeholder='请输入JSON格式的数据数组，例如：[{"skuName":"商品名称", "category":"分类","title":"标题","content":"内容","spuid":"123","materialUrl":"http://...","itemId":"456","guidePrice":"100","isActive":true}]'
                                    clearable />
                            </el-form-item>
                        </template>
                    </el-form>
                </div>

                <div class="modal-footer">
                    <button @click="closeSyncModal" class="btn btn-secondary enhanced-btn">
                        取消
                    </button>
                    <button @click="startSync" class="btn btn-primary enhanced-btn" :disabled="loading">
                        <div v-if="loading" class="loading-spinner"></div>
                        {{ loading ? '同步中...' : '开始同步' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    getAllPromotionContent,
    getAllPromotionContentByCategory,
    getAllPromotionContentCategories,
    createPromotionContent,
    updatePromotionContent,
    hardDeletePromotionContent,
    batchUpdatePromotionContentStatus,
    queryGoods,
    getPromotionContentByInvalidStatus,
    handleInvalidReport
} from '@/utils/jdApi'
import axios from 'axios'
import { quillEditor } from 'vue-quill-editor'
import { copyToClipboardWithMessage } from '@/utils/clipboard'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

export default {
    name: 'GuideManagement',
    components: {
        quillEditor
    },
    data() {
        return {
            contentList: [],
            categories: [],
            selectedItems: [],
            currentPage: 0,
            totalPages: 0,
            totalElements: 0,
            pageSize: 50,

            // 搜索条件
            searchCategory: '',
            searchKeyword: '',
            searchInvalidStatus: '', // 失效状态筛选

            // 弹窗状态
            showAddModal: false,
            showEditModal: false,
            showViewModal: false,
            showSyncModal: false,
            viewData: null, // 查看弹窗的数据
            showSyncPreviewModal: false,

            // 同步配置信息
            syncConfig: {
                syncType: 'api', // 'api' 或 'url' 或 'data'
                token: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE3NjIxNDEzMzgsImV4cCI6MTc5MzY3NzMzOCwiZGF0YSI6eyJwaWQiOjMxMDExNDAwMDUsInR5cGUiOjAsImNpZCI6NDEwMTMzNTAyNiwidWlkIjozNSwiY29tbWlzc2lvbl9yYXRlIjowfX0.oE_PLm6L2W0TtEZ8zpVjAHl8QJ1qhWKj4mNm6BJtyvg',
                maxPage: '1', // 使用字符串，支持输入如 "1,2,3" 或 "1\n2\n3" 的格式
                productUrl: '/api/guide/proxy/product',
                courseUrl: '/api/guide/proxy/course',
                keyword: '', // 链接同步使用的关键词
                iptContent: '', // 链接同步使用的内容
                dataArray: '' // 数据同步使用的数据数组 JSON 字符串
            },

            // 同步预览数据
            syncPreviewData: [],
            // 同步预览选中的数据
            selectedSyncData: [],
            // 同步失败列表
            syncFailedList: [],
            // 新增的itemId列表（变量名保持createdSpuids以兼容现有代码，但实际存储的是itemId）
            createdSpuids: [],

            // 表单验证规则
            formRules: {
                category: [
                    { required: true, message: '请选择分类', trigger: 'change' },
                    {
                        validator: (rule, value, callback) => {
                            if (value === 'custom' && !this.customCategory) {
                                callback(new Error('请输入自定义分类'))
                            } else {
                                callback()
                            }
                        }, trigger: 'change'
                    }
                ],
                content: [
                    { required: true, message: '请输入内容', trigger: 'blur' }
                ],
            },
            // 同步表单验证规则
            syncRules: {},

            // 表单数据
            formData: {
                category: '',
                title: '',
                content: '',
                spuid: '',
                materialUrl: '',
                itemId: '',
                guidePrice: null,
                isActive: true
            },
            customCategory: '',
            editId: null,
            loading: false,
            // 富文本编辑器配置
            editorOption: {
                placeholder: '请输入内容...',
                theme: 'snow',
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'header': [1, 2, 3, false] }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        ['link', 'image'],
                        ['clean']
                    ]
                }
            }
        }
    },
    computed: {
        isAllSelected() {
            return this.contentList.length > 0 && this.selectedItems.length === this.contentList.length
        }
    },
    watch: {
        // 监听同步类型变化，更新同步表单验证规则
        'syncConfig.syncType': {
            handler() {
                this.updateSyncRules()
            },
            immediate: true
        }
    },
    mounted() {
        this.loadContentList()
        this.loadCategories()
    },
    methods: {
        /**
         * 更新同步表单验证规则
         */
        updateSyncRules() {
            const rules = {}
            if (this.syncConfig.syncType === 'api') {
                rules.token = [
                    { required: true, message: 'Token不能为空', trigger: 'blur' }
                ]
                rules.maxPage = [
                    { required: true, message: '页数数组不能为空', trigger: 'blur' },
                    {
                        validator: (rule, value, callback) => {
                            const maxPageArray = this.parseMaxPage(value)
                            if (!maxPageArray || maxPageArray.length === 0) {
                                callback(new Error('请输入有效的页数'))
                            } else if (maxPageArray.some(page => !Number.isInteger(page) || page < 1)) {
                                callback(new Error('所有页数必须为正整数'))
                            } else {
                                callback()
                            }
                        }, trigger: 'blur'
                    }
                ]
                rules.productUrl = [
                    { required: true, message: '商品接口地址不能为空', trigger: 'blur' }
                ]
                rules.courseUrl = [
                    { required: true, message: '课程接口地址不能为空', trigger: 'blur' }
                ]
            } else if (this.syncConfig.syncType === 'url') {
                rules.keyword = [
                    { required: true, message: '关键词不能为空', trigger: 'blur' }
                ]
                rules.iptContent = [
                    { required: true, message: '内容不能为空', trigger: 'blur' }
                ]
            } else if (this.syncConfig.syncType === 'data') {
                rules.dataArray = [
                    { required: true, message: '数据数组不能为空', trigger: 'blur' },
                    {
                        validator: (rule, value, callback) => {
                            if (!value || !value.trim()) {
                                callback(new Error('数据数组不能为空'))
                                return
                            }
                            try {
                                const parsed = JSON.parse(value.trim())
                                if (!Array.isArray(parsed)) {
                                    callback(new Error('请输入有效的数组格式，例如：[{...}]'))
                                } else if (parsed.length === 0) {
                                    callback(new Error('数据数组不能为空'))
                                } else {
                                    callback()
                                }
                            } catch (error) {
                                callback(new Error('请输入有效的JSON格式'))
                            }
                        }, trigger: 'blur'
                    }
                ]
            }
            this.syncRules = rules
            // 清除表单验证，因为规则已更改
            this.$nextTick(() => {
                if (this.$refs.syncForm) {
                    this.$refs.syncForm.clearValidate()
                }
            })
        },
        /**
         * 显示同步配置弹窗
         */
        showSyncConfig() {
            this.showSyncModal = true
            // 确保规则已更新
            this.updateSyncRules()
        },

        /**
         * 关闭同步配置弹窗
         */
        closeSyncModal() {
            this.showSyncModal = false
            // 清除表单验证
            if (this.$refs.syncForm) {
                this.$refs.syncForm.clearValidate()
            }
        },

        /**
         * 开始同步（先获取数据，显示预览）
         */
        async startSync() {
            // 表单验证
            try {
                await this.$refs.syncForm.validate()
            } catch (error) {
                return
            }

            this.closeSyncModal()
            this.loading = true

            try {
                let allAddData = []

                // 根据同步类型调用不同的方法
                if (this.syncConfig.syncType === 'api') {
                    // 接口同步模式
                    allAddData = await this.fetchSyncData()
                } else if (this.syncConfig.syncType === 'url') {
                    // 链接同步模式
                    allAddData = await this.fetchSyncDataByUrl()
                } else if (this.syncConfig.syncType === 'data') {
                    // 数据同步模式：直接解析用户输入的数据数组
                    try {
                        const dataArrayStr = this.syncConfig.dataArray.trim()
                        allAddData = JSON.parse(dataArrayStr)
                    } catch (error) {
                        console.error('解析数据数组失败:', error)
                        this.$message?.error?.('解析数据失败: ' + ('请检查JSON格式是否正确'))
                        return
                    }
                }

                if (allAddData.length === 0) {
                    this.$message?.warning?.('没有可同步的数据')
                    this.loading = false
                    return
                }

                // 显示预览弹窗
                this.syncPreviewData = allAddData
                this.selectedSyncData = [] // 初始化选中数据
                this.showSyncPreviewModal = true
                // 默认全选所有数据
                this.$nextTick(() => {
                    this.$refs.syncPreviewTable?.toggleAllSelection()
                })
            } catch (error) {
                console.error('获取同步数据失败:', error)
                this.$message?.error?.('获取同步数据失败: ' + ('未知错误'))
            } finally {
                this.loading = false
            }
        },

        /**
         * 链接同步模式：通过关键词查询商品
         */
        async fetchSyncDataByUrl() {
            const keyword = this.syncConfig.keyword.trim()
            const allAddData = []

            // 支持多个关键词（换行分隔）
            const keywords = keyword.split('\n').filter(k => k.trim()).map(k => k.trim())

            for (const kw of keywords) {
                try {
                    // 使用封装好的接口
                    const response = await queryGoods({ keyword: kw })

                    if (response && response.code === 200) {
                        // API返回结构: { code: 200, data: [...], totalCount: ... }
                        let goodsList = []
                        if (response.data && Array.isArray(response.data)) {
                            goodsList = response.data
                        } else if (response.data && Array.isArray(response.data.data)) {
                            goodsList = response.data.data
                        }
                        for (const goods of goodsList) {
                            // 获取商品图片（用于content中显示）
                            let imageUrl = null
                            if (goods.imageInfo && goods.imageInfo.imageList && goods.imageInfo.imageList.length > 0) {
                                imageUrl = goods.imageInfo.imageList[0].url
                            }

                            allAddData.push({
                                category: goods.categoryInfo.cid3Name,
                                content: this.syncConfig.iptContent,
                                guidePrice: '',
                                isActive: true,
                                itemId: goods.oriItemId || goods.itemId || null,
                                materialUrl: goods.materialUrl || null,
                                spuid: goods.spuid || null,
                                title: goods.brandName || '',
                                imageUrl: imageUrl,
                                skuName: goods.skuName
                            })
                        }
                    } else {
                        console.warn(`关键词 "${kw}" 查询失败:`, response.data)
                    }

                    // 多个关键词之间添加延迟
                    if (keywords.indexOf(kw) < keywords.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300))
                    }
                } catch (error) {
                    console.error(`关键词 "${kw}" 查询失败:`, error)
                    // 继续处理下一个关键词，不中断
                }
            }
            return allAddData
        },

        /**
         * 关闭同步预览弹窗
         */
        closeSyncPreviewModal() {
            this.showSyncPreviewModal = false
            this.syncPreviewData = []
            this.selectedSyncData = []
            this.createdSpuids = []
        },

        /**
         * 处理同步预览表格的选择变化
         */
        handleSyncSelectionChange(selection) {
            this.selectedSyncData = selection
        },

        /**
         * 复制新增的ItemId列表到剪贴板
         */
        async copyCreatedSpuids() {
            if (!this.createdSpuids || this.createdSpuids.length === 0) {
                this.$message?.warning?.('没有可复制的内容')
                return
            }

            // 将数组转换为逗号分隔的字符串
            const textToCopy = this.createdSpuids.join(', ')

            try {
                await copyToClipboardWithMessage(
                    textToCopy,
                    this,
                    '已复制新增的ItemId到剪贴板',
                    '复制失败，请手动复制'
                )
            } catch (error) {
                console.error('复制失败:', error)
                this.$message?.error?.('复制失败，请手动复制')
            }
        },

        /**
         * 确认同步（保存数据）
         */
        async confirmSync() {
            // 如果有选中的数据，使用选中的数据；否则使用全部数据
            let dataToSync = this.selectedSyncData.length > 0 ? this.selectedSyncData : this.syncPreviewData

            if (dataToSync.length === 0) {
                this.$message?.warning?.('没有可同步的数据')
                return
            }

            // 处理每条数据，如果没有"更新时间"，则添加
            dataToSync = dataToSync.map(item => {
                const processedItem = { ...item }
                // 检查内容中是否已经包含"更新时间"
                if (!this.hasUpdateTime(processedItem.content)) {
                    // 如果没有"更新时间"，则添加
                    const updateTime = this.formatTimeForSave()
                    const updateTimeText = `更新时间：${updateTime}`

                    // 判断是否为 HTML 格式
                    if (processedItem.content && processedItem.content.includes('<')) {
                        // HTML 格式：在开头添加 <p>更新时间：...</p>
                        processedItem.content = `<p>${updateTimeText}</p>${processedItem.content || ''}`
                    } else {
                        // 纯文本格式：在开头添加
                        processedItem.content = `${updateTimeText}\n${processedItem.content || ''}`
                    }
                }
                return processedItem
            })

            this.loading = true
            try {
                const res = await createPromotionContent(dataToSync)
                if (res.success) {
                    // 保存新增的itemId列表（createdSpuids实际返回的是itemId）
                    this.createdSpuids = res.createdItemIds || res.createdSpuids || []

                    let message = '同步成功，共处理 ' + (res.count || dataToSync.length) + ' 条记录（新增 ' + (res.createdCount || 0) + ' 条，更新 ' + (res.updatedCount || 0) + ' 条）'
                    this.$message?.success?.(message)

                    // 不立即关闭弹窗，让用户可以看到新增的itemId列表
                    // 刷新列表
                    await this.loadContentList()
                } else {
                    this.$message?.error?.('同步失败')
                }
            } catch (error) {
                this.$message?.error?.('同步失败: ' + ('未知错误'))
            } finally {
                this.loading = false
            }
        },

        /**
         * 解析maxPage字符串为数组
         * 支持逗号分隔或换行分隔，如 "1,2,3" 或 "1\n2\n3"
         */
        parseMaxPage(maxPageStr) {
            if (!maxPageStr || typeof maxPageStr !== 'string') {
                return []
            }
            // 支持逗号和换行符分隔
            const pages = maxPageStr.split(/[,\n]/)
                .map(page => page.trim())
                .filter(page => page !== '')
                .map(page => parseInt(page, 10))
                .filter(page => !isNaN(page) && page > 0)
            // 去重并排序
            return [...new Set(pages)].sort((a, b) => a - b)
        },

        /**
         * 获取同步数据（不保存）
         */
        async fetchSyncData() {
            // 使用配置的值
            const token = this.syncConfig.token
            const maxPageArray = this.parseMaxPage(this.syncConfig.maxPage)
            const productUrl = this.syncConfig.productUrl
            const courseUrl = this.syncConfig.courseUrl

            const allSpuids = []
            const allCourseData = []
            const allAddData = []

            // 步骤1: 根据页数数组循环获取所有spuid
            for (const pageNum of maxPageArray) {
                try {
                    const response = await axios.get(productUrl, {
                        headers: { 'Token': token },
                        params: { page: pageNum }
                    })

                    if (response.data && response.data.code === 200) {
                        const data = response.data.data
                        if (data.goods && Array.isArray(data.goods)) {
                            const pageSpuids = data.goods
                                .filter(item => item && item.skuId)
                                .map(item => item.skuId)
                            allSpuids.push(...pageSpuids)
                        }
                        // 每页请求之间添加延迟
                        if (maxPageArray.indexOf(pageNum) < maxPageArray.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 200))
                        }
                    } else {
                        console.warn(`第 ${pageNum} 页请求失败:`, response.data?.msg || '请求失败')
                    }
                } catch (error) {
                    console.error(`获取第 ${pageNum} 页spuid失败:`, error)
                    // 继续处理下一页，不中断整个流程
                }
            }
            console.log('获取到的spuid列表，共', allSpuids.length, '个:', allSpuids);

            // 步骤2: 使用spuid数组循环请求course接口
            let successCount = 0
            let failCount = 0
            this.failedSkuIds = []

            for (let i = 0; i < allSpuids.length; i++) {
                const skuId = String(allSpuids[i])
                try {
                    const response = await axios.post(courseUrl, {
                        action: 'detail',
                        param: {
                            skuId: skuId,
                            fields: 'coupon'
                        }
                    }, {
                        headers: {
                            'Token': token,
                            'Content-Type': 'application/json'
                        }
                    })

                    if (response.data && response.data.code === 200 && response.data.data) {
                        const courseData = response.data.data

                        if (!courseData.hkj || !courseData.hkj.course) {
                            failCount++
                            this.failedSkuIds.push(skuId)
                            continue
                        }

                        allCourseData.push(courseData)

                        // 处理content内容：替换特定的HTML段落
                        let content = courseData.hkj.course || ''
                        if (content.includes('✅必领红包可膨胀') || content.includes('必领红包可膨胀')) {
                            const oldUrl = 'https://u.jd.com/0gJMpaK'
                            const newUrl = 'https://u.jd.com/TayVS40'
                            const escapedOldUrl = oldUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                            content = content.replace(new RegExp(escapedOldUrl, 'gi'), newUrl)
                        }
                        const oldPattern = '用京东app，直接扫码领券加购物车。<br/>（不要用“微信”直接扫码！！）'
                        const newContent = '<p>使用京东app，复制下单链接到首页搜索框打开！</p>'
                        // 先检查是否包含关键文本，再进行替换
                        if (content.includes('直接扫码领券加购物车')) {
                            content = content.replace(oldPattern, newContent)
                        }
                        let imageUrl = null
                        if (courseData.imageInfo && courseData.imageInfo.imageList && courseData.imageInfo.imageList.length > 0) {
                            imageUrl = courseData.imageInfo.imageList[0].url
                        }

                        allAddData.push({
                            category: courseData.categoryInfo.cid3Name,
                            content: content,
                            guidePrice: null,
                            isActive: true,
                            itemId: courseData.oriItemId || courseData.itemId,
                            materialUrl: courseData.materialUrl || null,
                            spuid: courseData.spuid || skuId,
                            title: courseData.brandName || '',
                            imageUrl: imageUrl,
                            skuName: courseData.skuName,
                        })
                        successCount++
                    } else {
                        failCount++
                        this.failedSkuIds.push(skuId)
                    }

                    // 添加延迟
                    if (i < allSpuids.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300))
                    }
                } catch (error) {
                    console.error(`请求course失败，skuId: ${skuId}`, error)
                    failCount++
                    this.failedSkuIds.push(skuId)

                    // 继续处理下一个，不中断
                    if (i < allSpuids.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300))
                    }
                }
            }

            console.log(`同步数据统计 - 总spuid数: ${allSpuids.length}, 成功: ${successCount}, 失败: ${failCount}`)
            console.log('成功的courseData数量:', allCourseData.length)
            console.log('准备保存的allAddData数量:', allAddData.length)
            // 返回数据，不立即保存
            return allAddData
        },
        goBack() {
            this.$router.go(-1)
        },
        // 加载内容列表
        async loadContentList() {
            try {
                this.loading = true
                let response

                // 如果指定了失效状态，优先使用失效状态查询
                if (this.searchInvalidStatus) {
                    response = await getPromotionContentByInvalidStatus(
                        this.searchInvalidStatus,
                        this.currentPage,
                        this.pageSize
                    )
                    if (response && response.success) {
                        let allContent = response.data || []

                        // 前端分类过滤
                        if (this.searchCategory) {
                            allContent = allContent.filter(item => item.category === this.searchCategory)
                        }

                        // 前端关键字过滤
                        if (this.searchKeyword) {
                            allContent = this.filterByKeyword(allContent, this.searchKeyword)
                        }

                        this.contentList = allContent
                        this.totalElements = response.totalElements || allContent.length
                        this.totalPages = response.totalPages || Math.ceil(allContent.length / this.pageSize)
                    } else {
                        this.$message?.error?.('加载失败')
                        this.contentList = []
                        this.totalElements = 0
                        this.totalPages = 0
                    }
                } else {
                    // 管理员获取所有数据（包括禁用状态）
                    if (this.searchCategory) {
                        response = await getAllPromotionContentByCategory(this.searchCategory)
                    } else {
                        response = await getAllPromotionContent({ page: this.currentPage, size: this.pageSize })
                    }

                    if (response.success) {
                        let allContent = response.data || []

                        // 前端关键字过滤
                        if (this.searchKeyword) {
                            allContent = this.filterByKeyword(allContent, this.searchKeyword)
                        }

                        // 前端失效状态过滤（如果未使用失效状态查询）
                        if (!this.searchInvalidStatus) {
                            // 不筛选，显示所有
                        } else {
                            allContent = allContent.filter(item =>
                                item.invalidStatus === this.searchInvalidStatus
                            )
                        }

                        // 分页处理
                        const startIndex = this.currentPage * this.pageSize
                        const endIndex = startIndex + this.pageSize
                        this.contentList = allContent.slice(startIndex, endIndex)
                        this.totalElements = allContent.length
                        this.totalPages = Math.ceil(allContent.length / this.pageSize)
                    }
                }
            } catch (error) {
                this.$message?.error?.('加载失败')
            } finally {
                this.loading = false
            }
        },

        // 加载分类列表
        async loadCategories() {
            try {
                // 管理员获取所有分类（包括禁用状态的分类）
                const response = await getAllPromotionContentCategories()
                if (response.success) {
                    this.categories = response.data || []
                }
            } catch (error) {
                console.error('加载分类失败:', error)
            }
        },

        // 确保分类在列表中
        ensureCategoryInList(category) {
            if (category && !this.categories.includes(category)) {
                this.categories.push(category)
            }
        },

        // 前端关键字过滤
        filterByKeyword(contentList, keyword) {
            if (!keyword || !keyword.trim()) {
                return contentList
            }

            const searchKeyword = keyword.toLowerCase().trim()
            return contentList.filter(item => {
                // 在标题中搜索
                const titleMatch = item.title && item.title.toLowerCase().includes(searchKeyword)
                // 在内容中搜索
                const contentMatch = item.content && item.content.toLowerCase().includes(searchKeyword)
                // 在分类中搜索
                const categoryMatch = item.category && item.category.toLowerCase().includes(searchKeyword)

                return titleMatch || contentMatch || categoryMatch
            })
        },

        // 搜索处理
        handleSearch() {
            this.currentPage = 0
            this.loadContentList()
        },

        // 处理表格选择变化
        handleSelectionChange(selection) {
            this.selectedItems = selection.map(item => item.id)
        },

        // 获取行样式类名
        getRowClassName({ row }) {
            if (this.selectedItems.includes(row.id)) {
                return 'selected-row'
            }
            return ''
        },

        // 检查内容中是否包含"更新时间"
        hasUpdateTime(content) {
            if (!content) return false

            // 如果是 HTML 格式，先提取纯文本内容
            let textContent = content
            if (content.includes('<')) {
                // 创建临时 DOM 元素来提取纯文本
                const tempDiv = document.createElement('div')
                tempDiv.innerHTML = content
                textContent = tempDiv.textContent || tempDiv.innerText || ''
            }

            // 检查是否包含"更新时间："（支持冒号是中文或英文）
            // 匹配模式：更新时间、更新时间：、更新时间:（支持中文和英文冒号）
            const updateTimePattern = /更新时间[：:]\s*\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/g
            if (updateTimePattern.test(textContent)) {
                return true
            }
            // 如果包含"更新时间"字符串，也认为是已有更新时间
            return textContent.includes('更新时间')
        },

        // 提取纯内容（移除"更新时间："行）
        extractPureContent(content) {
            if (!content) return ''
            // 如果内容以"更新时间："开头，移除这一行
            const lines = content.split('\n')
            if (lines[0] && lines[0].trim().startsWith('更新时间')) {
                return lines.slice(1).join('\n').trim()
            }
            return content
        },

        // 查看内容
        viewContent(item) {
            this.viewData = { ...item } // 复制数据，避免直接修改原数据
            this.showViewModal = true
        },

        // 关闭查看弹窗
        closeViewModal() {
            this.showViewModal = false
            this.viewData = null
        },

        editContent(item) {
            this.editId = item.id
            // 保持原内容不变，不移除"更新时间："行（如果存在的话）
            // 这样在保存时，如果已经有"更新时间"，就不会被替换

            this.formData = {
                category: item.category,
                title: item.title,
                content: item.content || '',
                spuid: item.spuid || '',
                materialUrl: item.materialUrl || '',
                itemId: item.itemId || '',
                guidePrice: item.guidePrice || null,
                isActive: item.isActive
            }

            // 确保分类数据已加载，如果当前分类不在列表中，添加到列表
            this.ensureCategoryInList(item.category)

            this.showEditModal = true
        },

        // 删除内容
        async deleteContent(id) {
            if (confirm('确定要删除这条推广内容吗？删除后将无法恢复！')) {
                try {
                    const response = await hardDeletePromotionContent(id)
                    if (response.success) {
                        this.$message?.success?.('删除成功')
                        this.loadContentList()
                    }
                } catch (error) {
                    console.error('删除失败:', error)
                    this.$message?.error?.('删除失败')
                }
            }
        },

        // 批量删除
        async batchDelete() {
            if (this.selectedItems.length === 0) {
                this.$message?.warning?.('请选择要删除的内容')
                return
            }

            if (confirm(`确定要删除选中的 ${this.selectedItems.length} 条内容吗？删除后将无法恢复！`)) {
                try {
                    for (const id of this.selectedItems) {
                        await hardDeletePromotionContent(id)
                    }
                    this.$message?.success?.('批量删除成功')
                    this.selectedItems = []
                    this.loadContentList()
                } catch (error) {
                    console.error('批量删除失败:', error)
                    this.$message?.error?.('批量删除失败')
                }
            }
        },

        // 批量更新状态
        async batchUpdateStatus(isActive) {
            if (this.selectedItems.length === 0) {
                this.$message?.warning?.('请选择要操作的内容')
                return
            }

            try {
                const response = await batchUpdatePromotionContentStatus(this.selectedItems, isActive)

                if (response.success) {
                    this.selectedItems = []
                    this.loadContentList()
                }
            } catch (error) {
                console.error('批量更新失败:', error)
                this.$message?.error?.('批量更新失败')
            }
        },

        // 格式化时间用于保存
        formatTimeForSave() {
            const now = new Date()
            const year = now.getFullYear()
            const month = String(now.getMonth() + 1).padStart(2, '0')
            const day = String(now.getDate()).padStart(2, '0')
            const hours = String(now.getHours()).padStart(2, '0')
            const minutes = String(now.getMinutes()).padStart(2, '0')
            const seconds = String(now.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        },

        // 保存内容
        async saveContent() {
            // 表单验证
            try {
                await this.$refs.contentForm.validate()
                // 如果是自定义分类，验证自定义分类名称
                if (this.formData.category === 'custom' && !this.customCategory) {
                    this.$message?.error?.('请输入自定义分类')
                    return
                }
            } catch (error) {
                return
            }

            this.loading = true
            try {
                let response
                let finalContent = this.formData.content

                // 检查内容中是否已经包含"更新时间"
                if (!this.hasUpdateTime(this.formData.content)) {
                    // 如果没有"更新时间"，则添加
                    const updateTime = this.formatTimeForSave()
                    const updateTimeText = `更新时间：${updateTime}`

                    // 判断是否为 HTML 格式
                    if (this.formData.content && this.formData.content.includes('<')) {
                        // HTML 格式：在开头添加 <p>更新时间：...</p>
                        finalContent = `<p>${updateTimeText}</p>${this.formData.content}`
                    } else {
                        // 纯文本格式：在开头添加
                        finalContent = `${updateTimeText}\n${this.formData.content}`
                    }
                }
                // 如果已经包含"更新时间"，则不做任何处理，保持原样

                const data = {
                    ...this.formData,
                    category: this.formData.category === 'custom' ? this.customCategory : this.formData.category,
                    content: finalContent
                }

                if (this.showEditModal && this.editId) {
                    response = await updatePromotionContent(this.editId, data)
                } else {
                    response = await createPromotionContent([data])
                }

                if (response.success) {
                    this.$message?.success?.(this.showEditModal ? '更新成功' : '创建成功')
                    this.closeModal()
                    this.loadContentList()
                }
            } catch (error) {
                console.error('保存失败:', error)
                this.$message?.error?.('保存失败')
            } finally {
                this.loading = false
            }
        },

        // 关闭弹窗
        closeModal() {
            this.showAddModal = false
            this.showEditModal = false
            this.formData = {
                category: '',
                title: '',
                content: '',
                spuid: '',
                materialUrl: '',
                itemId: '',
                guidePrice: null,
                isActive: true
            }
            this.customCategory = ''
            this.editId = null
            // 清除表单验证
            if (this.$refs.contentForm) {
                this.$refs.contentForm.clearValidate()
            }
        },


        // el-pagination 页面变化处理
        handlePageChange(page) {
            this.currentPage = page - 1
            this.loadContentList()
        },

        // el-pagination 页面大小变化处理
        handleSizeChange(size) {
            this.pageSize = size
            this.currentPage = 0
            this.loadContentList()
        },

        // 格式化时间
        formatTime(timeStr) {
            if (!timeStr) return ''
            const date = new Date(timeStr)
            return date.toLocaleString('zh-CN')
        },

        // 获取失效状态文本
        getInvalidStatusText(status) {
            const statusMap = {
                'NONE': '未上报',
                'REPORTED': '待处理',
                'CONFIRMED': '已处理',
                'REJECTED': '已驳回'
            }
            return statusMap[status] || '未知'
        },

        // 获取失效状态标签类型
        getInvalidStatusType(status) {
            const typeMap = {
                'NONE': 'info',
                'REPORTED': 'warning',
                'CONFIRMED': 'success',
                'REJECTED': 'danger'
            }
            return typeMap[status] || 'info'
        },

        // 确认失效上报
        async confirmInvalidReport(row) {
            try {
                this.$confirm('确认该条信息已失效？', '确认失效', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                }).then(async () => {
                    try {
                        const response = await handleInvalidReport(row.id, 'CONFIRMED', '管理员确认失效')
                        if (response && response.success) {
                            this.$message.success('确认失效成功')
                            this.loadContentList()
                        } else {
                            this.$message.error('确认失败')
                        }
                    } catch (error) {
                        console.error('确认失效失败:', error)
                        this.$message.error('确认失败')
                    }
                }).catch(() => {
                    // 用户取消
                })
            } catch (error) {
                console.error('处理失败:', error)
            }
        },

        // 驳回失效上报
        async rejectInvalidReport(row) {
            try {
                this.$prompt('请输入驳回原因', '驳回失效上报', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    inputPlaceholder: '请输入驳回原因'
                }).then(async ({ value }) => {
                    try {
                        const response = await handleInvalidReport(row.id, 'REJECTED', value || '管理员驳回')
                        if (response && response.success) {
                            this.$message.success('驳回成功')
                            this.loadContentList()
                        } else {
                            this.$message.error('驳回失败')
                        }
                    } catch (error) {
                        console.error('驳回失败:', error)
                        this.$message.error('驳回失败')
                    }
                }).catch(() => {
                    // 用户取消
                })
            } catch (error) {
                console.error('处理失败:', error)
            }
        },

        // 预览图片
        previewImage(imageUrl) {
            if (imageUrl) {
                window.open(imageUrl, '_blank')
            }
        }
    }
}
</script>

<style scoped lang="scss">
.promotion-content-management {
    margin: 0 auto;
    padding: 10px;
    background: #f8f9fa;
    height: 100vh;
    box-sizing: border-box;
}

.header {
    background: linear-gradient(135deg, #42b983, #369870);
    color: white;
    padding: 4px;
    margin-bottom: 10px;
    flex-shrink: 0;
    border-radius: 12px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.back-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: white;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.back-btn:hover {
    background: rgba(255, 255, 255, 0.2);
}

.back-btn svg {
    width: 20px;
    height: 20px;
}

.title {
    font-size: 18px;
    font-weight: 600;
    color: white;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding: 10px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-wrap: wrap;
    gap: 15px;
}

.toolbar-left {
    width: 100%;
    display: flex;
    gap: 10px;
    justify-content: space-between;
}

.toolbar-right {
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.search-filters {
    width: 100%;
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.filter-select,
.search-input {
    border-radius: 4px;
    font-size: 14px;
    width: 100%;
}

.search-input {
    min-width: 200px;
}

.btn {
    flex: 1;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;
    align-items: center;
    gap: 5px;
    line-height: 24px;
    justify-content: center;
}

.btn-primary {
    background: #42b983;
    color: white;
}

.btn-primary:hover {
    background: #369870;
}

.btn-danger {
    background: #dc3545;
    color: white;
}

.btn-danger:hover {
    background: #c82333;
}

.btn-success {
    background: #28a745;
    color: white;
}

.btn-success:hover {
    background: #218838;
}

.btn-warning {
    background: #ffc107;
    color: #212529;
}

.btn-warning:hover {
    background: #e0a800;
}

.btn-secondary {
    background: #42b983;
    color: white;
}

.btn-secondary:hover {
    background: #42b983;
    color: white;
}

.btn-outline {
    background: transparent;
    color: #42b983;
    border: 1px solid #42b983;
}

.btn-outline:hover {
    background: #42b983;
    color: white;
}

.btn-info {
    background: #17a2b8;
    color: white;
}

.btn-info:hover {
    background: #138496;
}

.btn-sm {
    padding: 4px 8px;
    font-size: 12px;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.content-table-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* el-table 自定义样式 */
.content-table-container .el-table {
    border: none;
}

/* 自定义滚动条样式 */
.content-table-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.content-table-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.content-table-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.content-table-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Firefox 滚动条样式 */
.content-table-container {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
}

.content-table-container .el-table th {
    background: #f8f9fa;
    color: #495057;
    font-weight: 600;
    border-bottom: 2px solid #dee2e6;
}

.content-table-container .el-table td {
    border-bottom: 1px solid #f1f3f4;
}

.content-table-container .el-table tbody tr:hover {
    background-color: #f8f9fa;
}

.content-table-container .el-table .selected-row {
    background-color: #e3f2fd;
}

.content-table-container .el-table .selected-row:hover {
    background-color: #e3f2fd;
}

/* 操作按钮样式 */
.content-table-container .el-button--mini {
    padding: 5px 8px;
    margin: 0 2px;
}

/* 分页容器样式 */
.pagination-container {
    display: flex;
    justify-content: center;
    padding: 10px;
    background: white;
    border-top: 1px solid #f1f3f4;
}

.content-table-container {
    overflow-x: auto;
    overflow-y: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .difContent {
        height: calc(100vh - 180px) !important;
    }

    .content-table-container {
        max-height: calc(100vh - 210px);
        overflow-x: auto;
        overflow-y: auto;
    }

    .content-table-container .el-table {
        min-width: 600px;
    }

    .content-table-container .el-button--mini {
        padding: 3px 6px;
        font-size: 12px;
    }

    .pagination-container {
        padding: 15px 10px;
    }

    .pagination-container .el-pagination {
        flex-wrap: wrap;
    }
}

/* 小屏幕设备优化 */
@media (max-width: 480px) {

    .content-table-container .el-table {
        min-width: 450px;
    }
}

.category-tag {
    background: #e3f2fd;
    color: #1976d2;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
}

.status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
}

.status-badge.active {
    background: #d4edda;
    color: #155724;
}

.status-badge.inactive {
    background: #f8d7da;
    color: #721c24;
}




.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease-out;
    backdrop-filter: blur(4px);
    width: 100%;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-content {
    background: white;
    border-radius: 16px;
    width: 90%;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease-out;
    position: relative;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.enhanced-modal {
    border: 1px solid rgba(66, 185, 131, 0.2);
    box-shadow: 0 25px 80px rgba(66, 185, 131, 0.15);
}

.view-modal {
    max-width: 800px;
}

.view-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.view-item {
    display: flex;
    align-items: flex-start;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
}

.view-item:last-child {
    border-bottom: none;
}

.view-item label {
    font-weight: 600;
    color: #2c3e50;
    min-width: 100px;
    margin-right: 12px;
    flex-shrink: 0;
}

.view-item span {
    color: #606266;
    flex: 1;
    word-break: break-word;
}

.view-content-text {
    width: 100%;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    min-height: 100px;
    max-height: 400px;
    overflow-y: auto;
    line-height: 1.6;
    color: #2c3e50;
}

.view-content-text img {
    max-width: 100%;
    height: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #f8fffe 0%, #f0f9f6 100%);
    border-radius: 16px 16px 0 0;
}

.modal-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 16px;
    font-weight: 600;
}

.title-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #42b983, #369870);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.modal-header h3 {
    margin: 0;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
}

.modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #6c757d;
    padding: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.modal-close:hover {
    color: #495057;
    background: #f8f9fa;
}


.modal-body {
    padding: 28px;
    height: calc(100vh - 280px);
    overflow-y: auto;
}

.enhanced-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    color: #2c3e50;
    font-size: 14px;
}

.label-text {
    font-weight: 600;
}

.required-mark {
    color: #e74c3c;
    font-weight: 600;
}

.select-wrapper {
    position: relative;
}

.form-select {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #e1e8ed;
    border-radius: 8px;
    font-size: 14px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    appearance: none;
    outline: none;
}

.form-select:focus {
    border-color: #42b983;
    box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.select-arrow {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6c757d;
}


.form-input {
    width: 100%;
    font-size: 14px;
    outline: none;
    background: white;
}

.form-input:focus {
    border-color: #42b983;
    box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.form-input::placeholder {
    color: #a0aec0;
}

.custom-category-input {
    margin-top: 8px;
}

.input-counter {
    text-align: right;
    font-size: 12px;
    color: #6c757d;
    margin-top: 4px;
}

.textarea-wrapper {
    position: relative;
}

.form-textarea {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #e1e8ed;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
    outline: none;
    background: white;
    resize: vertical;
    min-height: 120px;
    font-family: inherit;
    box-sizing: border-box;
}

.form-textarea:focus {
    border-color: #42b983;
    box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
}

.form-textarea::placeholder {
    color: #a0aec0;
}

.textarea-counter {
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-size: 12px;
    color: #6c757d;
    background: white;
    padding: 2px 6px;
    border-radius: 4px;
}

.checkbox-group {
    margin-top: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    font-weight: 500;
    color: #2c3e50;
}

.form-checkbox {
    display: none;
}

.checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid #e1e8ed;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    position: relative;
}

.form-checkbox:checked+.checkbox-custom {
    background: #42b983;
    border-color: #42b983;
}

.form-checkbox:checked+.checkbox-custom::after {
    content: '';
    width: 6px;
    height: 10px;
    border: 2px solid white;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
    position: absolute;
}

.checkbox-text {
    font-size: 14px;
}

/* 新字段样式 */
.material-link {
    color: #42b983;
    text-decoration: none;
    word-break: break-all;
}

.material-link:hover {
    text-decoration: underline;
}

.price-value {
    color: #e74c3c;
    font-weight: 600;
    font-size: 16px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 24px 28px;
    border-top: 1px solid #f0f0f0;
    background: #fafbfc;
    border-radius: 0 0 16px 16px;
}

.enhanced-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
}

.enhanced-btn.btn-primary {
    background: linear-gradient(135deg, #42b983, #369870);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(66, 185, 131, 0.3);
}

.enhanced-btn.btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #369870, #2d7a5a);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(66, 185, 131, 0.4);
}

.enhanced-btn.btn-secondary {
    background: white;
    color: #6c757d;
    border: 2px solid #e1e8ed;
}

.enhanced-btn.btn-secondary:hover {
    background: #f8f9fa;
    border-color: #d1d5db;
    color: #495057;
}

.enhanced-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

.loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .modal-content {
        width: 95%;
        margin: 10px;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 10px;
    }

    .enhanced-form {
        gap: 10px;
    }

    .enhanced-btn {
        width: 100%;
        justify-content: center;
    }
}

/* 富文本编辑器样式 */
.content-editor-wrapper {
    width: 100%;
}

.content-quill-editor {
    width: 100%;
}

.content-quill-editor .ql-toolbar {
    padding: 4px 8px;
    border: 1px solid #dcdfe6;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    background-color: #fafafa;
}

.content-quill-editor .ql-container {
    border: 1px solid #dcdfe6;
    border-top: none;
    border-radius: 0 0 4px 4px;
    font-size: 14px;
    min-height: 120px;
    max-height: 200px;
    overflow-y: auto;
}

.content-quill-editor .ql-editor {
    min-height: 120px;
    max-height: 200px;
    padding: 8px;
    overflow-y: auto;
}

.content-quill-editor .ql-editor.ql-blank::before {
    font-style: normal;
    color: #c0c4cc;
}

::v-deep {
    .el-radio-button__orig-radio:checked+.el-radio-button__inner {
        background-color: #42b983;
        border-color: #42b983;
        color: #fff;
        box-shadow: none;
    }

    .el-radio-button__inner:hover {
        color: #42b983;
    }
}
</style>